/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DatePicker, DatePickerInput, TimePicker } from 'carbon-addons-iot-react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useField } from 'formik';
import isNil from 'lodash/isNil';
import { injectIntl } from 'react-intl';
import get from 'lodash/get';
import moment from 'moment/min/moment-with-locales';

import {
  formatDate,
  formatTime,
  parseDate,
  parseTime,
  validateTimePattern,
  validateDatePattern,
} from 'formFunctions';
import { FormLabelTooltip } from 'Common';

const DateTimePickerFormikPropTypes = {
  /* The form field name where this will be captured */
  name: PropTypes.string.isRequired,
  formikValues: PropTypes.objectOf(PropTypes.any),
  /* An object that shows which fields in the form have been touched */
  touched: PropTypes.objectOf(PropTypes.any),
  hideLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  datePickerClassName: PropTypes.string,
  timePickerClassName: PropTypes.string,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  learnMore: PropTypes.string,
  /** If true, wait for form submission to show validation error * */
  validateOnSubmission: PropTypes.bool,
  validate: PropTypes.func,
  formMeta: PropTypes.objectOf(PropTypes.any),
};

const DateTimePickerFormikDefaultProps = {
  label: null,
  formikValues: {},
  touched: {},
  datePickerClassName: null,
  disabled: false,
  hideLabel: false,
  required: false,
  timePickerClassName: null,
  tooltip: null,
  learnMore: null,
  validateOnSubmission: false,
  validate: null,
  formMeta: null,
};

export function calculateTime(parsedDate, parsedTime) {
  parsedDate.subtract(parsedDate.local().hours(), 'h');
  parsedDate.subtract(parsedDate.local().minutes(), 'm');
  parsedDate.add(parsedTime.local().hours(), 'h');
  parsedDate.add(parsedTime.local().minutes(), 'm');
  return parsedDate.valueOf();
}

/**
 *
 * Still prepends "datePickerRedux-" to the name for the DatePicker side of the form field.
 * TimePicker name stays true
 */
const DateTimePickerFormik = props => {
  const {
    datePickerClassName,
    timePickerClassName,
    validate,
    required,
    name,
    label,
    tooltip,
    learnMore,
    formikValues,
    touched,
    hideLabel,
    disabled,
    intl: { locale }, // eslint-disable-line no-unused-vars
  } = props;
  const [dateField, dateFieldMeta, dateFieldHelpers] = useField({
    name: `datePickerRedux-${name}`,
  });
  const [timeField, timeFieldMeta, timeFieldHelpers] = useField({ name });

  const { name: dateFieldName } = dateField;
  const { name: timeFieldName } = timeField;

  const date = get(formikValues, dateFieldName);
  const time = get(formikValues, timeFieldName);

  const dateVisited = touched[dateFieldName];
  const timeVisited = touched[timeFieldName];

  /**
   * Only keep the second part of the locale data in specific cases (like for Great Britain's date format)
   * because Carbon's DatePicker will throw proptypes errors if we don't split it. Issue open here: https://github.com/carbon-design-system/carbon/issues/6380
   */
  const updatedLocale = locale === 'en-gb' ? locale : locale.split('-')[0];

  const labelComponent = (
    <FormLabelTooltip label={label} required={required} tooltip={tooltip} learnMore={learnMore} />
  );

  // If have a timeInput value but no dateInput value I need to set the dateInput value in the form
  useEffect(
    () => {
      if (!dateVisited && typeof time === 'number' && isNil(date)) {
        // Manually initialize the dateField with no hours or minutes
        const parsedDate = moment(time);
        parsedDate.subtract(parsedDate.hours(), 'h');
        parsedDate.subtract(parsedDate.minutes(), 'm');
        dateFieldHelpers.setValue(parsedDate.valueOf());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateFieldName, date, time, dateVisited]
  );

  useEffect(
    () => {
      // If I have a date but not a time, I want to initialize the time field
      if (!timeVisited && isNil(time) && !isNil(date) && typeof date === 'number') {
        timeFieldHelpers.setValue(date);
      } else if (typeof time === 'number' && dateVisited && typeof date === 'number') {
        // If I have just set the date field, and the time field is value, I need to update it
        timeFieldHelpers.setValue(calculateTime(moment(date), moment(time)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, time, timeFieldName, timeVisited]
  );

  let dateFormatDatePicker = '';
  // set moment locale
  moment.locale(updatedLocale);
  // get current date format eg. MM/DD/YYYY
  const currentDateFormat = moment.localeData().longDateFormat('L');

  // format for DatePicker flatpickr format https://flatpickr.js.org/formatting/
  currentDateFormat.split('/').forEach((item, index) => {
    const format =
      item.charAt(0) === 'D' || item.charAt(0) === 'M'
        ? item.charAt(0).toLowerCase()
        : item.charAt(0);
    dateFormatDatePicker = `${dateFormatDatePicker}${index !== 0 ? '/' : ''}${format}`;
  });

  const handleDatePickerChange = event => {
    const formattedDate = formatDate(moment(event[0]), updatedLocale);
    const parsedDate = validateDatePattern(formattedDate)
      ? parseDate(formattedDate, updatedLocale)
      : formattedDate;
    dateFieldHelpers.setValue(calculateTime(parsedDate, moment(time)));
    // Have to force blur to trigger the validation
    document.getElementById(dateField.name).blur();
    // reset time field when new date is picked
    timeFieldHelpers.setValue(Number(moment(event[0]).format('x')));
  };

  const handleTimePickerChange = event => {
    if (event.target.value.length === 5) {
      try {
        const formattedTime = formatTime(
          moment(`${moment(date).format('L')} ${event.target.value}`)
        );
        const parsedTime = validateTimePattern(formattedTime)
          ? parseTime(formattedTime, updatedLocale)
          : formattedTime;
        timeFieldHelpers.setValue(calculateTime(moment(date), parsedTime));
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <>
      <DatePicker
        onChange={handleDatePickerChange}
        id={`${dateField.name}_date-picker`}
        datePickerType="single"
        value={dateField.value}
        dateFormat={dateFormatDatePicker}
        // Need this until we fix the parsing here
        allowInput={false}
        light
        locale={updatedLocale}>
        <DatePickerInput
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...dateField}
          id={dateField.name}
          disabled={disabled}
          aria-label={label}
          labelText={labelComponent}
          iconDescription={label}
          autoComplete="off"
          onChange={dateField.onChange}
          onBlur={validate}
          value={
            !isNil(dateField.value)
              ? moment(dateField.value).isValid()
                ? moment(dateField.value).format('L')
                : dateField.value
              : ''
          }
          invalid={!!dateFieldMeta.error}
          invalidText={dateFieldMeta.error}
          hideLabel={hideLabel}
          className={datePickerClassName}
          placeholder={moment.localeData().longDateFormat('L')}
          title={label}
          label={label}
        />
      </DatePicker>

      <TimePicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...timeField}
        disabled={disabled}
        labelText={labelComponent}
        autoComplete="off"
        id={timeField.name}
        onChange={handleTimePickerChange}
        value={
          !isNil(timeField.value)
            ? moment(timeField.value).isValid()
              ? moment(timeField.value).format('HH:mm')
              : timeField.value
            : ''
        }
        hideLabel
        className={timePickerClassName}
        invalid={!!timeFieldMeta.error}
        invalidText={timeFieldMeta.error}
        title={label}
        aria-label={label}
      />
    </>
  );
};

DateTimePickerFormik.propTypes = DateTimePickerFormikPropTypes;
DateTimePickerFormik.defaultProps = DateTimePickerFormikDefaultProps;

export default injectIntl(DateTimePickerFormik);
